interface Styles {
	error: string;
	warning: string;
	info: string;
	success: string;
	debug: string;
}

interface Emojis {
	error: string;
	warning: string;
	info: string;
	success: string;
	debug: string;
}

interface Logger {
	styles: Styles;
	emojis: Emojis;
	error: (...args: unknown[]) => void;
	warning: (...args: unknown[]) => void;
	info: (...args: unknown[]) => void;
	success: (...args: unknown[]) => void;
	debug: (...args: unknown[]) => void;
	group: (label: string) => void;
	groupEnd: () => void;
	table: (data: unknown) => void;
}

class LoggerImplementation implements Logger {
	public readonly styles: Styles = {
		error: 'color: #ff0000; font-weight: bold',
		warning: 'color: #ffa500; font-weight: bold',
		info: 'color: #0066ff; font-weight: bold',
		success: 'color: #00cc00; font-weight: bold',
		debug: 'color: #808080; font-weight: bold'
	};

	public readonly emojis: Emojis = {
		error: '🚫',
		warning: '⚠️',
		info: 'ℹ️',
		success: '✅',
		debug: '🔍'
	};

	private getTimestamp(): string {
		return new Date().toLocaleTimeString();
	}

	public error(...args: unknown[]): void {
		console.log(
			`%c${this.emojis.error} [ERROR][${this.getTimestamp()}]:`,
			this.styles.error,
			...args
		);
	}

	public warning(...args: unknown[]): void {
		console.log(
			`%c${this.emojis.warning} [WARNING][${this.getTimestamp()}]:`,
			this.styles.warning,
			...args
		);
	}

	public info(...args: unknown[]): void {
		console.log(`%c${this.emojis.info} [INFO][${this.getTimestamp()}]:`, this.styles.info, ...args);
	}

	public success(...args: unknown[]): void {
		console.log(
			`%c${this.emojis.success} [SUCCESS][${this.getTimestamp()}]:`,
			this.styles.success,
			...args
		);
	}

	public debug(...args: unknown[]): void {
		if (process.env.NODE_ENV === 'development') {
			console.log(
				`%c${this.emojis.debug} [DEBUG][${this.getTimestamp()}]:`,
				this.styles.debug,
				...args
			);
		}
	}

	public group(label: string): void {
		console.group(label);
	}

	public groupEnd(): void {
		console.groupEnd();
	}

	public table(data: unknown): void {
		console.table(data);
	}
}

// Create a frozen instance of the logger
const logger = Object.freeze(new LoggerImplementation());

export default logger;
